/**
 * reviewState.js
 * PIPE-SAVE-1 F-3 (2026-07-09). The pure decision the interview review screen renders from. After an
 * auto-save (spoken done-signal) the app stays on the review screen in a "saved" state instead of
 * flashing past the chapter to a generic celebration: the Save button is replaced by a truthful
 * "Saved to your book" badge, a low-prominence "Remove this story" appears, and everything else on the
 * screen (chapter text, Flick/Whole chooser, photo/edit affordances) is untouched. A chapter is
 * "saved" only when BOTH the flag and a real savedStoryId are present. Kept here (not inline in
 * StRecordPage) so it has a real unit test — those files cannot load in Jest (react-pdf ESM).
 */
/**
 * SAVE-IDENTITY-1 (F-236, 2026-07-26). Is the content on screen the content that was saved under
 * `savedStoryId`, or merely content sitting on an object that once held a saved chapter?
 *
 * F-234: a customer lost a chapter because the old rule ("a flag and an id are present") is a
 * question about PROCESS STATE. `stProcessed` survives navigation, and story B was generated by
 * merging onto a stale object (App.js:5488 / :5563), inheriting `saved: true` + chapter A's
 * `savedStoryId`. The reveal went green, the Save button vanished, and the Flick/Whole toggle
 * silently wrote story B over chapter A.
 *
 * `generationId` is minted every time a generation produces new story content; `savedGenerationId`
 * is stamped only at a real save. So identity travels WITH the artifact, and a future entry or exit
 * path cannot poison it by forgetting to clear a flag — which is exactly how `reveal-see-book` and
 * `reveal-done` poisoned it. Edits and the Flick/Whole switch do NOT regenerate, so the stamp still
 * matches and State 2 keeps working.
 *
 * Fails SAFE: when a generation is present but unstamped, saved is FALSE, which offers Save and
 * creates a new row. A duplicate chapter is recoverable; an overwritten one is not.
 *
 * DOES NOT COVER: an object carrying no `generationId` at all (a legacy stProcessed from a session
 * already open at deploy time, and the DEV-only reveal fixtures) falls back to the flag+id rule.
 */
function hasSaveIdentity(stProcessed) {
  if (!stProcessed || !stProcessed.generationId) return true; // legacy/dev object: old rule
  return stProcessed.savedGenerationId === stProcessed.generationId;
}

export function getReviewMode(stProcessed) {
  const saved = !!(stProcessed && stProcessed.saved === true && stProcessed.savedStoryId
    && hasSaveIdentity(stProcessed)); // SAVE-IDENTITY-1: this content, not just some content
  // F-7: post-save edits (title/story/version) mark the saved story dirty. While dirty, the "Saved"
  // chip becomes an actionable "Save changes" (persists via the same shape-guarded seam), and
  // beforeunload warns. A clean saved story never warns.
  const dirty = saved && !!(stProcessed && stProcessed.dirty);
  return {
    saved,
    dirty,
    showSaveButton: !saved,              // unsaved: create (Save to My Book)
    showSaveChanges: dirty,              // saved + edited: persist the update
    showSavedBadge: saved && !dirty,     // saved + clean: quiet confirmation chip
    canRemove: saved,                    // low-prominence "Remove this story"
  };
}

/**
 * REVEAL-002 R1 — the UNIFIED-FINISH reveal classifier (founder 2026-07-11), layered on getReviewMode.
 *
 * THE LAW: a finish saves NOTHING. So an UNSAVED review object (a live server-side draft) is Reveal
 * State 0 — the AMBER "your story is ready · not saved to your book yet" landing, where Save / Edit /
 * Discard live. A SAVED object is the GREEN family: State 1 (revisit an already-saved chapter), State 2
 * (dirty post-save edit), State 3 (just-kept celebration). Amber-vs-green is the trust signal the ribbon
 * renders and the design contract asserts; it MUST track saved-ness exactly (an amber ribbon on a saved
 * chapter, or green on a draft, is a truth violation).
 *
 * opts.editing — the edit form is open (State 0-edit or State 2). opts.kept — the post-save celebration
 * beat (State 3). opts.resumed — this State 0 was re-entered from an existing draft (resume ribbon copy).
 */
export function getRevealMode(stProcessed, opts = {}) {
  const { editing = false, kept = false, resumed = false } = opts;
  const base = getReviewMode(stProcessed);
  const saved = base.saved;
  let state;
  if (!saved) state = editing ? 'ready-edit' : 'ready';   // State 0 / State 0 edit (a draft)
  else if (kept) state = 'kept';                          // State 3 (transient, right after Save)
  else state = editing ? 'saved-edit' : 'saved';          // State 1 revisit / State 2 dirty edit
  return {
    ...base,
    state,
    ribbon: saved ? 'green' : 'amber',       // amber = not saved (State 0), green = saved
    isReady: !saved,                         // State 0 family: a draft is on screen, not in the book
    isResumed: !saved && !!resumed,          // resume copy: "your story is still here"
    showReadyActions: !saved && !editing,    // Save / Edit / Discard live only in the State-0 landing
  };
}
